import { useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { resetRuler, setRulerSlot } from '@/hooks/useLiveNowSlot';

/** Single "live time" colour, shared with the highlighted block in LiveStatsText. */
export const LIVE_NOW_COLOR = '#764ba2';
export const LIVE_NOW_TINT = '#ede4f5';
/** The line/flag colour once the user has dragged it off live time (a ruler, not "now"). */
export const RULER_COLOR = '#6b7280';
const CHIP_HALF_W = 36;
/** Width of the invisible grab zone around the line — wide enough for a thumb. */
const GRAB_W = 28;

interface FlagProps {
  x: number;
  label: string;
  totalWidth: number;
  height: number;
  cellWidth: number;
  /** Ruler parked off live time. */
  pinned?: boolean;
  /** Wall-clock HHMM, for the "back to now" button. */
  liveLabel?: string | null;
}

/** The scrollable ancestor (the grid's x-scroller), for edge auto-scroll while dragging. */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  for (let p = el?.parentElement ?? null; p; p = p.parentElement) {
    const ox = getComputedStyle(p).overflowX;
    if ((ox === 'auto' || ox === 'scroll') && p.scrollWidth > p.clientWidth) return p;
  }
  return null;
}

/**
 * The "NOW 1145" flag, pinned to the top edge of the time header (inside it: the grid's scroll
 * container clips anything above, which cut the flag off) — and the handle for the movable ruler: press and drag the flag
 * or the strip around the line (mouse, finger or pen) to slide it along the time
 * axis, snapping to 5 minutes. It stays where it is let go (turns gray, showing the
 * time) until "↺ NOW" is pressed (or Esc / double-click on the flag).
 */
export function LiveNowFlag({ x, label, totalWidth, height, cellWidth, pinned = false, liveLabel = null }: FlagProps) {
  const color = pinned ? RULER_COLOR : LIVE_NOW_COLOR;
  const cx = Math.min(Math.max(x, CHIP_HALF_W + 24), Math.max(CHIP_HALF_W, totalWidth - CHIP_HALF_W - 24));
  const drag = useRef<{ lastX: number; raf: number; slotAt: (clientX: number) => number; offset: number; scroller: HTMLElement | null } | null>(null);

  const onDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (e.button > 0) return;
    const header = e.currentTarget.parentElement as HTMLElement;   // the position:relative time strip
    const scroller = scrollParent(header);
    const startScroll = scroller?.scrollLeft ?? 0;
    const baseLeft = header.getBoundingClientRect().left;
    // Slot under a pointer x, corrected for any auto-scroll since the strip was measured.
    const slotAt = (clientX: number) => (clientX - baseLeft + ((scroller?.scrollLeft ?? 0) - startScroll)) / cellWidth;
    // Keep the point that was grabbed under the finger instead of snapping the line's centre to it.
    const offset = slotAt(e.clientX) - x / cellWidth;
    const d = { lastX: e.clientX, raf: 0, slotAt, offset, scroller };
    drag.current = d;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    const apply = () => setRulerSlot(d.slotAt(d.lastX) - d.offset);
    apply();
    // Near either edge of the visible grid, keep scrolling so the line can be taken to
    // times that are off-screen (matters on a phone, where only ~5 hours show at once).
    const tick = () => {
      if (drag.current !== d) return;
      if (scroller) {
        const r = scroller.getBoundingClientRect();
        const rightZone = r.right - 48;
        const leftZone = r.left + 108;   // clear of the sticky label column
        if (d.lastX > rightZone) scroller.scrollLeft += Math.min(18, (d.lastX - rightZone) / 3 + 2);
        else if (d.lastX < leftZone) scroller.scrollLeft -= Math.min(18, (leftZone - d.lastX) / 3 + 2);
      }
      apply();
      d.raf = requestAnimationFrame(tick);
    };
    d.raf = requestAnimationFrame(tick);
  };
  const onMove = (e: ReactPointerEvent<HTMLElement>) => {
    if (drag.current) drag.current.lastX = e.clientX;
  };
  const onUp = (e: ReactPointerEvent<HTMLElement>) => {
    if (drag.current) { cancelAnimationFrame(drag.current.raf); drag.current = null; }
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };

  const grab: CSSProperties = { touchAction: 'none', cursor: 'ew-resize', userSelect: 'none', WebkitUserSelect: 'none' };
  const chip: CSSProperties = {
    position: 'absolute', top: 1, left: cx, transform: 'translateX(-50%)',
    background: color, color: '#fff', fontSize: 10, fontWeight: 700, lineHeight: 1,
    padding: '4px 6px', borderRadius: 4, whiteSpace: 'nowrap', zIndex: 7, display: 'flex', alignItems: 'center', gap: 6,
    fontFamily: 'system-ui, -apple-system, sans-serif', boxShadow: pinned ? '0 1px 4px rgba(0,0,0,0.3)' : undefined,
    ...grab,
  };
  return (
    <>
      <div aria-hidden style={{ position: 'absolute', left: x - 1, top: 0, width: 2, height, background: color, pointerEvents: 'none', zIndex: 5 }} />
      {/* Invisible, finger-wide grab strip over the line inside the time header. */}
      <div
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        title="Drag to measure a time"
        style={{ position: 'absolute', left: x - GRAB_W / 2, top: 0, width: GRAB_W, height, zIndex: 6, ...grab }}
      />
      <div
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
        onDoubleClick={resetRuler}
        title={pinned ? 'Ruler — drag to move' : 'Drag to move the line and read other times'}
        style={chip}
      >
        <span>{pinned ? label : `NOW ${label}`}</span>
        {pinned && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={resetRuler}
            title={liveLabel ? `Back to now (${liveLabel})` : 'Remove the ruler'}
            style={{ all: 'unset', cursor: 'pointer', background: 'rgba(255,255,255,0.25)', borderRadius: 3, padding: '2px 5px', fontSize: 10, fontWeight: 800 }}
          >
            {liveLabel ? `↺ NOW ${liveLabel}` : '✕'}
          </button>
        )}
      </div>
    </>
  );
}
